import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Building2, Mail, Phone, MapPin, Calendar, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCustomer = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}`, { withCredentials: true });
      setCustomer(response.data);
    } catch (error) {
      toast.error("Müşteri bulunamadı");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchCustomerAudits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audits`, { withCredentials: true });
      const customerAudits = response.data.filter(a => a.customer_id === id);
      setAudits(customerAudits);
    } catch (error) {
      console.error("Audits error:", error);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomer();
    fetchCustomerAudits();
  }, [fetchCustomer, fetchCustomerAudits]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-detail">
      <Button variant="ghost" onClick={() => navigate("/customers")} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Müşterilere Dön
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-[#E8F3EE]">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-[#1A4D2E] flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl text-[#111827]">{customer.name}</CardTitle>
                <span className="px-3 py-1 text-sm rounded-full bg-[#E8F3EE] text-[#1A4D2E]">
                  {customer.company_type || "Belirtilmemiş"}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customer.email && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB]">
                  <Mail className="w-5 h-5 text-[#1A4D2E]" />
                  <div>
                    <p className="text-xs text-[#9CA3AF]">E-posta</p>
                    <p className="text-[#111827]">{customer.email}</p>
                  </div>
                </div>
              )}
              {customer.phone && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB]">
                  <Phone className="w-5 h-5 text-[#1A4D2E]" />
                  <div>
                    <p className="text-xs text-[#9CA3AF]">Telefon</p>
                    <p className="text-[#111827]">{customer.phone}</p>
                  </div>
                </div>
              )}
              {customer.address && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB] md:col-span-2">
                  <MapPin className="w-5 h-5 text-[#1A4D2E]" />
                  <div>
                    <p className="text-xs text-[#9CA3AF]">Adres</p>
                    <p className="text-[#111827]">
                      {customer.address}
                      {customer.city && `, ${customer.city}`}
                      {customer.country && `, ${customer.country}`}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {customer.notes && (
              <div className="p-4 rounded-lg bg-[#F9FAFB]">
                <p className="text-xs text-[#9CA3AF] mb-1">Notlar</p>
                <p className="text-[#4B5563]">{customer.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#E8F3EE]">
          <CardHeader>
            <CardTitle className="text-lg text-[#111827]">Denetimler</CardTitle>
          </CardHeader>
          <CardContent>
            {audits.length === 0 ? (
              <p className="text-[#9CA3AF] text-center py-4">Henüz denetim yok</p>
            ) : (
              <div className="space-y-3">
                {audits.slice(0, 5).map((audit) => (
                  <div
                    key={audit.audit_id}
                    className="p-3 rounded-lg bg-[#F9FAFB] hover:bg-[#E8F3EE] cursor-pointer transition-colors"
                    onClick={() => navigate(`/audits/${audit.audit_id}`)}
                  >
                    <p className="font-medium text-[#111827] text-sm">{audit.title}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-[#9CA3AF]">
                      <Calendar className="w-3 h-3" />
                      {new Date(audit.scheduled_date).toLocaleDateString("tr-TR")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerDetail;
